<script setup lang="ts">

import {ChevronsLeft, Lock} from "lucide-vue-next";
import { PopoverArrow, PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from 'reka-ui'
import type { Ref } from "vue";
import {ref} from "vue";
import {Money} from "../../../utils/money";
import {label} from "../../mixins/translations.js";
import {useEnrollmentStore} from "../../stores/enrollment.ts";

interface Props {
    backUrl?: string|null,
}

const props = withDefaults(defineProps<Props>(), {})

const store = useEnrollmentStore();

const course: Ref<object> = ref(store.enrollment.item);

const openPopover: Ref<boolean> = ref(false);

</script>

<template>
    <div>
        <!-- HEADER -->
        <div class="flex items-start justify-between lg:flex-row flex-col gap-4 mb-6">
            <div>
                <a v-if="store.currentStep === 1" :href="backUrl ?? label('site.enrollment.different_course.link')" class="mt-5 inline-flex items-center gap-2 group">
                    <ChevronsLeft strokeWidth="1.5" class="h-6 w-6 transition-transform duration-300 group-hover:-translate-x-1 text-primary-400"
                    />
                    <span class="text-base text-gray-500">{{ label('site.enrollment.different_course') }}</span>
                </a>
                <a v-if="store.currentStep === 2" @click.prevent="store.moveToStep(1)" class="mt-5 inline-flex items-center gap-2 group">
                    <ChevronsLeft strokeWidth="1.5" class="h-6 w-6 transition-transform duration-300 group-hover:-translate-x-1 text-primary-400"
                    />
                    <span class="text-base text-gray-500">{{ label('site.enrollment.back_step_1') }}</span>
                </a>
                <a v-if="store.currentStep === 3" @click.prevent="store.moveToStep(2)" class="mt-5 inline-flex items-center gap-2 group cursor-pointer">
                    <ChevronsLeft strokeWidth="1.5" class="h-6 w-6 transition-transform duration-300 group-hover:-translate-x-1 text-primary-400"
                    />
                    <span class="text-base text-gray-500">{{ label('site.enrollment.back_step_2') }}</span>
                </a>


                <h1 class="text-2xl lg:text-3xl font-extrabold mt-3">
                    {{ course.product?.name ?? course.name }}
                </h1>
                <p class="text-base mt-2 text-gray-700" v-text="label('site.enrollment.enroll_in', {'course': course.product?.name ?? course.name})">
                </p>
            </div>

<!--            <div class="flex items-center gap-2 text-green-600 bg-success-50 text-sm py-2 px-4 font-semibold cursor-pointer" :title="label('site.enrollment.secure_connection.content')">-->
<!--                <Lock class="h-4 w-4" />-->
<!--                {{ label('site.enrollment.secure_connection') }}-->
<!--            </div>-->
            <PopoverRoot :open="openPopover">
                <PopoverTrigger @mouseover="openPopover = true" @mouseleave="openPopover = false">
                    <div class="flex items-center gap-2 text-base leading-relaxed bg-gray-50 py-2 px-4 cursor-pointer">
                        <Lock class="h-6 w-6 text-green-600" />
                        {{ label('site.enrollment.secure_connection') }}
                    </div>
                </PopoverTrigger>
                <PopoverPortal>
                    <PopoverContent
                        side="bottom"
                        :side-offset="5"
                        class="rounded-lg p-3 max-w-64 bg-white shadow-sm border will-change-[transform,opacity] data-[state=open]:data-[side=top]:animate-slideDownAndFade data-[state=open]:data-[side=right]:animate-slideLeftAndFade data-[state=open]:data-[side=bottom]:animate-slideUpAndFade data-[state=open]:data-[side=left]:animate-slideRightAndFade">
                        <p class="text-sm text-gray-700">
                            {{ label('site.enrollment.secure_connection.content') }}
                        </p>
                        <PopoverArrow class="fill-white stroke-gray-200" />
                    </PopoverContent>
                </PopoverPortal>
            </PopoverRoot>
        </div>

        <div class="rounded-lg border border-gray-300 py-8 px-16 grid gap-4 sm:grid-cols-3 mb-8">
            <div>
                <p class="text-base" v-text="label('site.enrollment.price')"></p>
                <p class="text-2xl font-extrabold">{{  Money.format(course.price.price_excl, course.price.currency, true) }}</p>
            </div>
            <div>
                <p class="text-base" v-text="label('site.enrollment.start_date')"></p>
                <p class="text-2xl font-extrabold" v-text="course.start_date"></p>
            </div>
            <div>
                <p class="text-base" v-text="label('site.enrollment.location')"></p>
                <p class="text-2xl font-extrabold" v-text="course.location.name"></p>
            </div>
        </div>

        <div class="mt-6 hidden md:block">
            <div class="grid grid-cols-1 md:grid-cols-3">
                <div class="flex items-center gap-3">
                    <div :class="{
                        'bg-primary text-white': store.currentStep >= 1,
                        'bg-gray-200 text-gray-900': store.currentStep < 1
                    }" class="flex h-8 w-8 items-center justify-center rounded-full text-base font-bold bg-primary text-white">
                        1
                    </div>
                    <span class="text-base font-semibold text-black" v-text="label('site.enrollment.step_1.header')"></span>
                </div>

                <div class="flex items-center gap-3">
                    <div :class="{
                        'bg-primary text-white': store.currentStep >= 2,
                        'bg-gray-200 text-gray-900': store.currentStep < 2
                    }" class="flex h-8 w-8 items-center justify-center rounded-full text-base font-bold">
                        2
                    </div>
                    <span class="text-base font-semibold text-black" v-text="label('site.enrollment.step_2.header')"></span>
                </div>

                <div class="flex items-center gap-3">
                    <div :class="{
                        'bg-primary text-white': store.currentStep >= 3,
                        'bg-gray-200 text-gray-900': store.currentStep < 3
                    }" class="flex h-8 w-8 items-center justify-center rounded-full text-base font-bold ">
                        3
                    </div>
                    <span class="text-base font-semibold text-black" v-text="label('site.enrollment.step_3.header')"></span>
                </div>
            </div>

            <div class="mt-4 h-[4px] w-full bg-gray-200 relative">
                <div :class="{
                        'w-[33.33%] bg-primary ': store.currentStep == 2,
                        'w-[66.66%] bg-primary ': store.currentStep == 3,
                    }" class="absolute left-0 top-0 h-full "></div>
            </div>
        </div>

    </div>
</template>
